import React, { useMemo } from 'react';
import { HelpCircle, Link2, RefreshCw } from 'lucide-react';
import { usePriceLinkage } from '../../hooks/usePriceLinkage';
import type { PriceLinkageSessionType } from '../../services/api';

interface PriceLinkageCardProps {
  ticker: string;
  sessionType?: PriceLinkageSessionType | string;
  windowDays?: number;
  enabled?: boolean;
}

const getLinkageTone = (label?: string): 'direct' | 'inverse' | 'neutral' => {
  const value = (label || '').toLowerCase();
  if (value.includes('inverse')) return 'inverse';
  if (value.includes('direct')) return 'direct';
  return 'neutral';
};

const getConfidenceTone = (label?: string): 'high' | 'medium' | 'low' => {
  const value = (label || '').toLowerCase();
  if (value.includes('high')) return 'high';
  if (value.includes('medium')) return 'medium';
  return 'low';
};

const getLinkageTextClass = (tone: 'direct' | 'inverse' | 'neutral'): string => {
  if (tone === 'direct') return 'text-[#4ff6e6]';
  if (tone === 'inverse') return 'text-orange-400';
  return 'text-white/70';
};

const getConfidenceBadgeClass = (tone: 'high' | 'medium' | 'low'): string => {
  if (tone === 'high') return 'bg-[#4ff6e6]/15 border-[#4ff6e6]/35 text-[#4ff6e6]';
  if (tone === 'medium') return 'bg-[#D4AF37]/15 border-[#D4AF37]/35 text-[#D4AF37]';
  return 'bg-white/10 border-white/20 text-white/75';
};

const formatNum = (value?: number) => (Number.isFinite(value) ? Number(value).toFixed(2) : 'N/A');
const formatInt = (value?: number) => (Number.isFinite(value) ? String(Math.round(Number(value))) : 'N/A');

const rawMetricPairRows = [
  [
    { label: 'Mentions correlation', key: 'corrMentions' as const, kind: 'num' as const },
    { label: 'Mentions best lag', key: 'bestLagMentions' as const, kind: 'lag' as const },
  ],
  [
    { label: 'Sentiment correlation', key: 'corrSentiment' as const, kind: 'num' as const },
    { label: 'Sentiment best lag', key: 'bestLagSentiment' as const, kind: 'lag' as const },
  ],
  [
    { label: 'Noise correlation', key: 'corrNoise' as const, kind: 'num' as const },
    { label: 'Noise best lag', key: 'bestLagNoise' as const, kind: 'lag' as const },
  ],
  [
    { label: 'Observations', key: 'nObs' as const, kind: 'int' as const },
    { label: 'Quality score', key: 'qualityScore' as const, kind: 'num' as const },
  ],
];

const PriceLinkageCardContent: React.FC<PriceLinkageCardProps> = ({
  ticker,
  sessionType = 'REGULAR',
  windowDays = 60,
}) => {
  const { data, isLoading, error, isEmpty, refresh } = usePriceLinkage(ticker, sessionType, windowDays);

  const linkageTone = useMemo(
    () => getLinkageTone(data?.readable?.linkageLabel),
    [data?.readable?.linkageLabel]
  );
  const confidenceTone = useMemo(
    () => getConfidenceTone(data?.readable?.confidenceLabel),
    [data?.readable?.confidenceLabel]
  );

  return (
    <div className="card-glass p-6 h-full min-h-[420px] flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Link2 className="w-5 h-5 text-brand-cyan" />
          Price Linkage
        </h3>
        <span className="text-xs text-white/60">Window: {windowDays}d</span>
      </div>

      {isLoading && (
        <div className="price-linkage-loading">
          <div className="h-8 rounded bg-white/10 w-48 mb-3" />
          <div className="h-6 rounded bg-white/10 w-24 mb-3" />
          <div className="h-4 rounded bg-white/10 w-full mb-2" />
          <div className="h-4 rounded bg-white/10 w-10/12" />
        </div>
      )}

      {!isLoading && error && (
        <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4">
          <p className="text-sm text-brand-red/90 mb-3">Price linkage unavailable right now.</p>
          <button type="button" onClick={() => refresh()} className="btn btn-outline text-sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && isEmpty && (
        <div className="bg-[#0d0d0d] border border-[#333] rounded-lg p-4 text-center">
          <p className="text-sm text-white/70">Not enough data yet.</p>
        </div>
      )}

      {!isLoading && !error && !isEmpty && data && (
        <>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <p className={`text-2xl leading-tight font-bold ${getLinkageTextClass(linkageTone)}`}>
              {data.readable.linkageLabel}
            </p>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-bold ${getConfidenceBadgeClass(confidenceTone)}`}>
              {data.readable.confidenceLabel}
            </span>
          </div>

          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-sm text-white/80">
              Lead time: {formatInt(data.readable.leadTimeHours)}h
            </span>
            <span
              className="inline-flex items-center"
              title="Estimated delay where linkage is strongest in historical window."
            >
              <HelpCircle className="w-4 h-4 text-white/50" />
            </span>
          </div>

          <p className="text-sm text-white/80 leading-relaxed mb-3">{data.readable.explanation}</p>

          <div className="space-y-2 mb-3">
            {rawMetricPairRows.map(([left, right]) => {
              const formatValue = (item: { key: keyof typeof data.raw; kind: 'num' | 'lag' | 'int' }) => {
                const rawValue = data.raw[item.key];
                if (item.kind === 'lag') return `${formatInt(rawValue)}h`;
                if (item.kind === 'int') return formatInt(rawValue);
                return formatNum(rawValue);
              };

              return (
                <div
                  key={`${left.key}-${right.key}`}
                  className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-white/65">{left.label}</span>
                    <strong className="text-white">{formatValue(left)}</strong>
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:border-l sm:border-white/10 sm:pl-3">
                    <span className="text-white/65">{right.label}</span>
                    <strong className="text-white">{formatValue(right)}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <p className="mt-auto text-xs text-white/55">
        Statistical linkage, not financial advice. Correlation can break over time.
      </p>
    </div>
  );
};

const PriceLinkageCard: React.FC<PriceLinkageCardProps> = (props) => {
  if (!props.enabled) {
    return (
      <div className="card-glass p-6 h-full min-h-[420px] flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-brand-cyan" />
            Price Linkage
          </h3>
          <span className="text-xs text-white/60">Window: {props.windowDays || 60}d</span>
        </div>
        <p className="text-sm text-white/70 mb-3">
          Premium feature. Unlock linkage between Reddit metrics and 1h price return.
        </p>
        <p className="mt-auto text-xs text-white/55">
          Statistical linkage, not financial advice. Correlation can break over time.
        </p>
      </div>
    );
  }
  return <PriceLinkageCardContent {...props} />;
};

export default PriceLinkageCard;
